import {useEffect, useState} from "react";
import {Check, PhoneCall, FileSearch, Package, MessageCircle, Gem, Store, Globe2} from "lucide-react";
import {apiGet, apiPost} from "@/lib/api";
import {useSite, Rich} from "@/lib/site";
import PurchaseReportModal, {SampleReportLink, REPORT_COVERS} from "@/pages/modals/PurchaseReport";

const EMPTY = {name: "", preferences: "", locations: "", phone: "", callback_time: ""};

export default function GetFromIndia({notify, requireAuth, user}) {
  const t = useSite();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("premium");
  const [pdr, setPdr] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const [form, setForm] = useState({...EMPTY, name: user?.name || ""});

  useEffect(() => {
    apiGet("/concierge/plans").then(setPlans).catch(() => {});
    apiGet("/settings/public").then(s => setWaNumber(s.whatsapp_number)).catch(() => {});
  }, []);
  const ch = e => setForm({...form, [e.target.name]: e.target.value});
  const plan = plans.find(p => p.plan_id === selected);

  const choose = (id) => {
    if (id === "purchase_report") { setPdr(true); return; }
    setSelected(id);
    document.getElementById("gfi-form")?.scrollIntoView({behavior: "smooth", block: "start"});
  };

  const submit = (e, channel = "call") => {
    e?.preventDefault();
    const formEl = document.querySelector('[data-testid="gfi-form"]');
    if (channel === "whatsapp" && !formEl.reportValidity()) return;
    requireAuth(async () => {
      try {
        await apiPost("/concierge", {...form, plan_id: selected, plan_name: plan?.name, support_type: plan?.name || selected, channel});
        setForm({...EMPTY, name: form.name});
        if (channel !== "whatsapp") { notify("Thank you! Your concierge will call you back shortly"); return; }
        if (!waNumber) { notify("Saved! WhatsApp is coming soon — we'll call you back instead"); return; }
        const text = `Hello! I'm ${form.name}. I'd like a call back for Your Personal Concierge.\nPlan: ${plan?.name || selected}\nNeed: ${form.preferences}\nDeliver to: ${form.locations}\nBest time: ${form.callback_time || "Any time"}`;
        window.location.href = `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
      } catch { notify("Please check the form and try again"); }
    });
  };

  const examples = [[Gem, t("gfi_example_1")], [Store, t("gfi_example_2")], [Globe2, t("gfi_example_3")]];

  return (
    <div data-testid="gfi-page">
      <div className="gfi-top">
        <div className="gfi-intro">
          <div className="eyebrow">YOUR PERSONAL CONCIERGE · FOR EVERYONE</div>
          <h1><Rich text={t("gfi_title")}/></h1>
          <p>{t("gfi_sub")}</p>
          <p className="gfi-purpose" data-testid="gfi-purpose">{t("gfi_purpose")}</p>
          <ul className="gfi-examples" data-testid="gfi-examples">
            {examples.map(([Icon, text], i) => <li key={i}><span><Icon size={15}/></span>{text}</li>)}
          </ul>
        </div>

        <form id="gfi-form" onSubmit={submit} className="gfi-form" data-testid="gfi-form">
          <div className="eyebrow">TELL US WHAT YOU NEED</div>
          <p className="gfi-form-plan">Plan: <b data-testid="gfi-selected-plan">{plan?.name || "Premium"}</b></p>
          <label>Your name
            <input data-testid="gfi-name" required minLength={2} name="name" value={form.name} onChange={ch} placeholder="Full name"/>
          </label>
          <label>How can we help?
            <textarea data-testid="gfi-needs" required name="preferences" value={form.preferences} onChange={ch} placeholder="e.g. Find a reliable plumber for my parents' flat, renew my car insurance, plan a birthday dinner"/>
          </label>
          <label>Where (city, country)
            <input data-testid="gfi-location" required name="locations" value={form.locations} onChange={ch} placeholder="Leicester, UK"/>
          </label>
          <div className="form-row">
            <label>Phone / WhatsApp
              <input data-testid="gfi-phone" required minLength={6} name="phone" type="tel" value={form.phone} onChange={ch} placeholder="+1 732..."/>
            </label>
            <label>Best time to call
              <input data-testid="gfi-callback" name="callback_time" value={form.callback_time} onChange={ch} placeholder="Weekends, 10am–1pm"/>
            </label>
          </div>
          <div className="callback-actions">
            <button data-testid="gfi-whatsapp" className="whatsapp-button" type="button" onClick={() => submit(null, "whatsapp")}><MessageCircle size={16}/> WhatsApp us</button>
            <button data-testid="gfi-submit" className="coral-button" type="submit"><PhoneCall size={15}/> Request a call back</button>
          </div>
        </form>
      </div>

      <div className="eyebrow" style={{marginTop: 34}}>CHOOSE A PLAN</div>
      <div className="gfi-plans" data-testid="gfi-plans">
        {plans.map(p => (
          <button type="button" key={p.plan_id} data-testid={`gfi-plan-${p.plan_id}`} className={selected === p.plan_id ? "plan-card selected" : "plan-card"} onClick={() => choose(p.plan_id)}>
            {selected === p.plan_id && <span className="plan-check"><Check size={12}/></span>}
            <span className="plan-icon">{p.plan_id === "purchase_report" ? <FileSearch size={18}/> : <Package size={18}/>}</span>
            <small className="plan-cadence">{p.cadence}</small>
            <h3>{p.name}</h3>
            <p>{p.blurb}</p>
            <span className="plan-cta">{p.plan_id === "purchase_report" ? "Request a report →" : "Choose →"}</span>
          </button>
        ))}
      </div>

      <section className="pdr-band" data-testid="gfi-pdr-band">
        <div>
          <div className="eyebrow">PURCHASE DECISION REPORT</div>
          <h2>Not sure it&rsquo;s worth it? <em>We&rsquo;ll find out.</em></h2>
          <div className="report-covers">{REPORT_COVERS.map(c => <span key={c}>{c}</span>)}</div>
        </div>
        <div className="pdr-actions">
          <SampleReportLink testId="gfi-download-sample"/>
          <button data-testid="gfi-request-report" className="coral-button" onClick={() => setPdr(true)}><FileSearch size={15}/> Request a report</button>
        </div>
      </section>

      {pdr && <PurchaseReportModal close={() => setPdr(false)} notify={notify} requireAuth={requireAuth}/>}
    </div>
  );
}
